/**
 * 冲突检测：轮廓约束 + 刀线点 vs 底座孔 + 组件间最小间隙。
 *
 * 返回 { hasConflict, conflictIds }：
 *  - outline：clampSelf 返回 true（R1 无法满足）
 *  - outline_in_baseHole：任一刀线点在底座孔内部（R2，只读）
 *  - component：两组件 shape 间距 < -minGapPx
 *
 * TODO: 性能优化 - 12 组件下若 60fps 掉帧，改为「动的组件 vs 其它静态」增量评估
 */

import { DRAW_TYPE, DEFAULT_CONFIG } from './constants.js';
import { getComponentType } from './typeRegistry.js';
import { shapeDistance } from './geometry/shapeDistance.js';
import { isPointInBaseHoleInterior } from './geometry/rotatedRect.js';

/**
 * R2：刀线顶点是否落入任一底座孔内部（只读，不修改组件位置）。
 * @param {Array<Array<number>>} outlinePoints - [[x,y],...]
 * @param {Array<Object>} components
 * @param {number} epsilonPx
 * @returns {string[]} 违规的 baseHole 组件 id 列表（去重、按 components 顺序）
 */
export function evaluateOutlinePointsInBaseHoles(
  outlinePoints,
  components,
  epsilonPx
) {
  const ids = [];
  if (!outlinePoints || !outlinePoints.length || !components) return ids;
  const eps =
    typeof epsilonPx === 'number' && epsilonPx >= 0
      ? epsilonPx
      : DEFAULT_CONFIG.baseHoleInteriorEpsilonPx;

  for (let c = 0; c < components.length; c++) {
    const inst = components[c];
    if (inst.type !== DRAW_TYPE.BASE_HOLE) continue;
    let hit = false;
    for (let i = 0; i < outlinePoints.length; i++) {
      const p = outlinePoints[i];
      if (isPointInBaseHoleInterior({ x: p[0], y: p[1] }, inst, eps)) {
        hit = true;
        break;
      }
    }
    if (hit) ids.push(inst.id);
  }
  return ids;
}

/**
 * 全量评估当前所有组件的冲突状态。
 * @param {Object} args
 * @param {Array<Object>} args.components - orchestrator 持有的组件实例数组
 * @param {Object} args.sharedState
 * @param {number} args.minGapPx
 * @param {string[]} [args.skipClampTypes] - 跳过 clampSelf 的 type（如立牌改刀线后不自动挪 baseHole）
 * @returns {{hasConflict:boolean, conflictIds:string[]}}
 */
export function evaluateAll({
  components,
  sharedState,
  minGapPx,
  skipClampTypes
}) {
  const conflictSet = new Set();
  if (!components || components.length === 0) {
    return { hasConflict: false, conflictIds: [] };
  }

  const skipSet = new Set(skipClampTypes || []);

  // 1) R1：轮廓约束（clampSelf 可能改位；skipClampTypes 中的 type 不执行）
  for (const inst of components) {
    if (skipSet.has(inst.type)) continue;
    const strategy = getComponentType(inst.type);
    if (!strategy || typeof strategy.clampSelf !== 'function') continue;
    const violated = strategy.clampSelf(inst, sharedState);
    if (violated) {
      conflictSet.add(inst.id);
    }
  }

  // 2) R2：刀线点不得在底座孔内（只读）
  const outlinePoints = sharedState.outlinePoints;
  const baseHoleViolations = evaluateOutlinePointsInBaseHoles(
    outlinePoints,
    components,
    DEFAULT_CONFIG.baseHoleInteriorEpsilonPx
  );
  for (let i = 0; i < baseHoleViolations.length; i++) {
    conflictSet.add(baseHoleViolations[i]);
  }

  // 3) 组件两两间距
  for (let i = 0; i < components.length; i++) {
    const a = components[i];
    const sa = getCollisionShape(a, sharedState);
    if (!sa) continue;
    for (let j = i + 1; j < components.length; j++) {
      const b = components[j];
      // 立牌在刀线外底部，底座孔必须在刀线内——立牌冲突已由 R1 覆盖，跳过对碰检测
      if (
        (a.type === DRAW_TYPE.LI_PAI && b.type === DRAW_TYPE.BASE_HOLE) ||
        (a.type === DRAW_TYPE.BASE_HOLE && b.type === DRAW_TYPE.LI_PAI)
      ) {
        continue;
      }
      const sb = getCollisionShape(b, sharedState);
      if (!sb) continue;
      const d = shapeDistance(sa, sb);
      // 浮点容差：右偏移放置间距恰好等于 minGapPx，浮点运算可能使 d 略小于 minGapPx 导致误报
      if (d < minGapPx - 0.01) {
        conflictSet.add(a.id);
        conflictSet.add(b.id);
      }
    }
  }

  const conflictIds = components
    .map(function (c) {
      return c.id;
    })
    .filter(function (id) {
      return conflictSet.has(id);
    });
  return { hasConflict: conflictIds.length > 0, conflictIds: conflictIds };
}

/**
 * 取组件碰撞形状（封装空保护）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {Object|null}
 */
function getCollisionShape(instance, sharedState) {
  const strategy = getComponentType(instance.type);
  if (!strategy || typeof strategy.getCollisionShape !== 'function')
    return null;
  return strategy.getCollisionShape(instance, sharedState);
}
